import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { outfitAPI, wardrobeAPI } from '../api';
import './OutfitsPage.css';

function OutfitsPage() {
  const navigate = useNavigate();
  const [outfits, setOutfits] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOccasion, setFilterOccasion] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  
  // Create outfit states
  const [isCreatingOutfit, setIsCreatingOutfit] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showOutfitModal, setShowOutfitModal] = useState(false);
  const [outfitName, setOutfitName] = useState('');

  useEffect(() => {
    loadOutfits();
    loadItems();
  }, []);

  const loadOutfits = async () => {
    try {
      setLoading(true);
      const response = await outfitAPI.getOutfits();
      const data = response.data;
      console.log('Loaded outfits:', data);
      setOutfits(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      console.error('Failed to load outfits:', err);
      setError('Failed to load outfits');
      setOutfits([]);
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    try {
      const response = await wardrobeAPI.getItems();
      const data = response.data;
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load wardrobe items:', err);
      setItems([]);
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        if (prev.length >= 3) {
          setError('Maximum 3 items per outfit');
          setTimeout(() => setError(''), 3000);
          return prev;
        }
        return [...prev, itemId];
      }
    });
  };

  const handleCreateOutfit = () => {
    if (selectedItems.length < 1) {
      setError('Please select at least 1 item to create an outfit');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (selectedItems.length > 3) {
      setError('Maximum 3 items allowed per outfit');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setShowOutfitModal(true);
  };

  const handleSaveOutfit = async () => {
    try {
      setLoading(true);
      await outfitAPI.createOutfit(
        outfitName.trim() || '',
        '',
        selectedItems,
        '',
        ''
      );
      
      setShowOutfitModal(false);
      setIsCreatingOutfit(false);
      setSelectedItems([]);
      setOutfitName('');
      
      await loadOutfits();
      
      setError('');
      alert('✅ Outfit created successfully! AI is generating metadata...');
    } catch (err) {
      console.error('Failed to create outfit:', err);
      setError(err.response?.data?.detail || 'Failed to create outfit');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOutfitCreation = () => {
    setIsCreatingOutfit(false);
    setSelectedItems([]);
    setShowOutfitModal(false);
    setOutfitName('');
  };

  const handleDeleteOutfit = async (outfitId) => {
    if (!window.confirm('Are you sure you want to delete this outfit?')) {
      return;
    }
    
    try {
      await outfitAPI.deleteOutfit(outfitId);
      await loadOutfits();
      alert('✅ Outfit deleted successfully!');
    } catch (err) {
      console.error('Failed to delete outfit:', err);
      setError('Failed to delete outfit');
    }
  };

  const handleToggleFavorite = async (outfit) => {
    try {
      await outfitAPI.updateOutfit(outfit.id, {
        is_favorite: !outfit.is_favorite
      });
      await loadOutfits();
    } catch (err) {
      console.error('Failed to update outfit:', err);
      setError('Failed to update outfit');
    }
  };

  const filteredOutfits = outfits.filter(outfit => {
    const matchesSearch = outfit.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (outfit.description && outfit.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesOccasion = !filterOccasion || (outfit.occasion && outfit.occasion.toLowerCase().includes(filterOccasion.toLowerCase()));
    const matchesSeason = !filterSeason || (outfit.season && outfit.season.toLowerCase().includes(filterSeason.toLowerCase()));
    return matchesSearch && matchesOccasion && matchesSeason;
  });
  return (
    <div 
      className="page-container"
      style={{ 
        backgroundImage: `url(${process.env.PUBLIC_URL}/assets/branding/hero-fashion.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="page-content">
        {/* Top Section Card */}
        <div className="top-section-card">
          <div className="header-top">
            <h2>My Outfits</h2>
            <button 
              className="btn btn-primary"
              onClick={() => setIsCreatingOutfit(!isCreatingOutfit)}
            >
              {isCreatingOutfit ? '✖ Cancel' : '✨ Create Outfit'}
            </button>
          </div>
        
        {!isCreatingOutfit && (
          <>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search outfits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filters">
              <select 
                value={filterOccasion} 
                onChange={(e) => setFilterOccasion(e.target.value)}
                className="filter-select"
              >
                <option value="">All Occasions</option>
                <option value="casual">Casual</option>
                <option value="work">Work</option>
                <option value="party">Party</option>
                <option value="formal">Formal</option>
                <option value="wedding">Wedding</option>
                <option value="date">Date</option>
                <option value="sport">Sport</option>
              </select>

              <select 
                value={filterSeason} 
                onChange={(e) => setFilterSeason(e.target.value)}
                className="filter-select"
              >
                <option value="">All Seasons</option>
                <option value="Spring">Spring</option>
                <option value="Summer">Summer</option>
                <option value="Fall">Fall</option>
                <option value="Autumn">Autumn</option>
                <option value="Winter">Winter</option>
              </select>
            </div>
          </>
        )}
        </div>

        {error && <div className="error-message">{error}</div>}

        {isCreatingOutfit ? (
          <div className="wardrobe-container">
          <div className="header-top">
            <h3>Select Items for Outfit</h3>
            <div className="outfit-actions">
              <button 
                className="btn btn-success btn-small"
                onClick={handleCreateOutfit}
                disabled={selectedItems.length < 1 || selectedItems.length > 3}
              >
                Next ({selectedItems.length})
              </button>
            </div>
          </div>
          
          <div className="selection-hint">
            Select 1-3 items to create an outfit ({selectedItems.length}/3)
          </div>

          {loading ? (
            <div className="loading">Loading items...</div>
          ) : (
            <div className="wardrobe-grid">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`wardrobe-item selectable ${selectedItems.includes(item.id) ? 'selected' : ''}`}
                  onClick={() => toggleItemSelection(item.id)}
                >
                  {selectedItems.includes(item.id) && (
                    <div className="selection-badge">
                      {selectedItems.indexOf(item.id) + 1}
                    </div>
                  )}
                  <img src={item.extracted_cloth_url} alt={item.item_name} />
                  <div className="item-info">
                    <h4>{item.item_name}</h4>
                    {item.item_type && <span className="tag">{item.item_type}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        ) : (
          <div className="wardrobe-container">
          {loading ? (
            <div className="loading">Loading outfits...</div>
          ) : filteredOutfits.length === 0 ? (
            <div className="empty-state">
              <p>No outfits found. Create your first outfit!</p>
            </div>
          ) : (
            <div className="outfits-grid">
              {filteredOutfits.map((outfit) => (
                <div key={outfit.id} className="outfit-card">
                  <div className="outfit-thumbnail" onClick={() => navigate(`/outfits/${outfit.id}`)}>
                    {outfit.thumbnail_url ? (
                      <img src={outfit.thumbnail_url} alt={outfit.name} />
                    ) : (
                      <div className="outfit-items-preview">
                        {outfit.items.slice(0, 3).map((item) => (
                          <img 
                            key={item.id} 
                            src={item.extracted_cloth_url} 
                            alt={item.item_name}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="outfit-header">
                    <h3 onClick={() => navigate(`/outfits/${outfit.id}`)}>{outfit.name}</h3>
                    <button
                      className={`favorite-btn ${outfit.is_favorite ? 'active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); handleToggleFavorite(outfit); }}
                    >
                      {outfit.is_favorite ? '★' : '☆'}
                    </button>
                  </div>

                  {outfit.description && (
                    <p className="outfit-description">{outfit.description}</p>
                  )}

                  <div className="outfit-details">
                    <div className="outfit-meta">
                      {outfit.occasion && (
                        <span className="tag tag-occasion">🎯 {outfit.occasion}</span>
                      )}
                      {outfit.season && (
                        <span className="tag tag-season">🌤️ {outfit.season}</span>
                      )}
                    </div>
                    <div className="outfit-items-count">
                      {outfit.items.length} item{outfit.items.length > 1 ? 's' : ''}
                    </div>
                  </div>

                  <button 
                    className="btn btn-secondary"
                    onClick={(e) => { e.stopPropagation(); handleDeleteOutfit(outfit.id); }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              ))}
            </div>
          )}
          </div>
        )}

      {showOutfitModal && (
        <div className="modal-overlay" onClick={() => setShowOutfitModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Outfit</h3>
            
            <div className="form-group">
              <p>
                Creating outfit with {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''}.
              </p>
              <p>
                AI will automatically analyze and generate:
              </p>
              <ul>
                <li>✨ Outfit name</li>
                <li>📝 Description</li>
                <li>🎯 Suitable occasions</li>
                <li>🌤️ Season recommendations</li>
              </ul>
            </div>

            <div className="form-group">
              <label>Custom Name (Optional)</label>
              <input
                type="text"
                placeholder="Leave empty for AI-generated name"
                value={outfitName}
                onChange={(e) => setOutfitName(e.target.value)}
                className="input-field"
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={handleCancelOutfitCreation}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSaveOutfit}
                disabled={loading}
              >
                {loading ? 'Creating... (This may take 30-60 seconds)' : 'Create Outfit'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default OutfitsPage;
